import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, ArrowRight, Loader2, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const STORAGE_KEY = 'bm_program_completed';

/** Full-screen congrats popup shown once a curated program hits its session target. */
export const ProgramCompletePopup = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [open, setOpen] = useState(false);
  const [continuing, setContinuing] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      setProgram(JSON.parse(raw));
      setOpen(true);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const dismiss = () => {
    localStorage.removeItem(STORAGE_KEY);
    setOpen(false);
  };

  const keepGoing = async () => {
    // Completion had parked the program; this puts the user back on it so training
    // carries straight on. No second congrats — the server keeps it celebrated.
    if (continuing) return;
    setContinuing(true);
    try {
      await api.post('/curated-presets/active/continue');
      localStorage.removeItem(STORAGE_KEY);
      setOpen(false);
      toast.success('Nice work — your training keeps rolling from here');
    } catch {
      toast.error("Couldn't continue the program. Please try again.");
    } finally {
      setContinuing(false);
    }
  };

  const switchProgram = () => {
    dismiss();
    navigate(user?.onboarding_complete ? '/custom-builder' : '/onboarding');
  };

  if (!program) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-5 bg-black/85 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="program-complete-popup"
        >
          <motion.div
            className="w-full max-w-sm rounded-3xl border-2 border-accent/40 bg-[#0B0B0B] overflow-hidden"
            style={{ boxShadow: '0 24px 70px rgba(0,0,0,0.7), inset 0 1px 0 rgba(var(--accent-rgb),0.15)' }}
            initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          >
            {program.hero_image_url && (
              <img src={program.hero_image_url} alt="" className="w-full h-36 object-cover" />
            )}
            <div className="p-6 text-center">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full border-2 border-accent flex items-center justify-center">
                <Trophy className="w-7 h-7 text-accent" />
              </div>
              <h2 className="font-heading font-black text-2xl uppercase tracking-tight text-white mb-1">
                Program Complete
              </h2>
              <p className="text-sm text-white/60 mb-1" data-testid="program-complete-name">{program.name}</p>
              <p className="text-sm text-accent font-semibold mb-6" data-testid="program-complete-sessions">
                {program.sessions_completed} of {program.target_sessions} sessions done
              </p>

              <div className="space-y-2.5">
                <Button
                  className="w-full h-12 font-heading font-bold uppercase tracking-wider text-black"
                  style={{ background: 'var(--accent-hex)' }}
                  onClick={keepGoing}
                  disabled={continuing}
                  data-testid="program-complete-keep-going"
                >
                  {continuing
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <ArrowRight className="w-4 h-4 mr-2" />}
                  Keep training {program.name}
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-12 font-heading font-bold uppercase tracking-wider border-white/25 text-white"
                  onClick={switchProgram}
                  data-testid="program-complete-switch"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Switch to Coach/Build
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
